import { useId, useRef, type HTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';
import { Panel } from './Panel';
import { useBodyScrollLock, useEscapeKey, useFocusTrap } from './overlayHooks';

export type ModalSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<ModalSize, string> = {
    sm: 'max-w-[380px]',
    md: 'max-w-[520px]',
    lg: 'max-w-[720px]',
};

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
    open: boolean;
    onClose: () => void;
    title: ReactNode;
    size?: ModalSize;
    dismissible?: boolean;
    /**
     * Override the modal's own stacking level (default `z-50`) — needed when
     * a modal must render above another full-screen overlay that isn't a
     * `<Modal>` itself, e.g. `ConfirmDialog` above `TutorialOverlay`'s
     * `z-[60]` spotlight backdrop. Leave unset for a normal, page-level
     * modal.
     */
    zIndexClassName?: string;
}

export function Modal({
    open,
    onClose,
    title,
    size = 'md',
    dismissible = true,
    zIndexClassName = 'z-50',
    className,
    children,
    ...rest
}: ModalProps) {
    const titleId = useId();
    const dialogRef = useRef<HTMLDivElement>(null);

    const dismiss = () => {
        if (dismissible) onClose();
    };

    useEscapeKey(open, dismiss);
    useFocusTrap(open, dialogRef);
    useBodyScrollLock(open);

    if (!open) return null;

    return createPortal(
        <div className={cn('fixed inset-0 flex items-center justify-center', zIndexClassName)}>
            <div
                className="fixed inset-0 bg-space-950/70 backdrop-blur-sm"
                onClick={dismiss}
                aria-hidden="true"
            />
            <Panel
                ref={dialogRef}
                variant="glass"
                padding="lg"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className={cn('relative z-10 w-full animate-pop-in', SIZE_CLASSES[size], className)}
                {...rest}
            >
                <h2 id={titleId} className="text-heading text-content-primary mb-3">
                    {title}
                </h2>
                {children}
            </Panel>
        </div>,
        document.body,
    );
}

export default Modal;
